import { useEffect } from "react";

import { apiClient } from "@/api/services";

import {
  applyOrgThemeCss,
  clearRuntimeBrandingStyleTags,
} from "./runtime-branding-css";
import type { IActiveThemeResponse, IResolvedBranding } from "./types";

interface IUseApplyOrgBrandingProps {
  currentProjectUuid: string | null;
  enabled: boolean;
  onBrandingResolved: (branding: IResolvedBranding) => void;
  onBrandingCleared: () => void;
}

export const useApplyOrgBranding = ({
  currentProjectUuid,
  enabled,
  onBrandingResolved,
  onBrandingCleared,
}: IUseApplyOrgBrandingProps): void => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let isCancelled = false;
    const abortController = new AbortController();

    apiClient
      .get<IActiveThemeResponse>("/theme/me", {
        signal: abortController.signal,
      })
      .then(({ data }) => {
        if (isCancelled) {
          return;
        }

        applyOrgThemeCss(data.cssContent);
        // NOTE: NSAI-6321 — the platform name and the favicon flag ride on the
        // same response as the CSS rather than getting a lookup of their own:
        // this call is on the boot path the first paint already waits for, and
        // the name has to be settled before the first tab title is written.
        onBrandingResolved({
          cssContent: data.cssContent,
          appName: data.appName ?? null,
          hasFavicon: !!data.hasFavicon,
          hasLogo: !!data.hasLogo,
        });
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        clearRuntimeBrandingStyleTags();
        onBrandingCleared();
      });

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [currentProjectUuid, enabled, onBrandingCleared, onBrandingResolved]);
};
